/* draft — keep work that hasn't been saved yet, and say which it is.

   The estimator warned before a refresh and the quick-add modal closed on a
   backdrop click. Neither KEPT anything: a phone call, a back gesture or a
   dropped connection took the customer's details and the traced geometry with
   it, and the browser's generic "leave site?" prompt was the only protection.

   This stores the in-progress form on the device under a named key and reports
   the state honestly. "Saved locally" is not "saved to the cloud" and the two
   are never printed as the same thing — a device-local draft is gone if the
   phone is. */

export type DraftState = "idle" | "unsaved" | "saving" | "local" | "error";

export const DRAFT_LABEL: Record<DraftState, string> = {
  idle: "", unsaved: "Unsaved", saving: "Saving…",
  local: "Saved on this device", error: "Couldn't save",
};

export type Draft<T> = { at: number; label: string; data: T };

export function readDraft<T = any>(key: string): Draft<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && d.data && Number(d.at) ? d : null;
  } catch (e) { return null; }
}

export function writeDraft<T = any>(key: string, data: T, label: string): DraftState {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), label, data }));
    return "local";
  } catch (e) {
    // a full or blocked store is the one case a draft badge must not lie about
    return "error";
  }
}

export function clearDraft(key: string) {
  try { localStorage.removeItem(key); } catch (e) {}
}

/** "3 minutes ago" for the resume banner */
export function draftAge(at: number) {
  const mins = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return mins + (mins === 1 ? " minute ago" : " minutes ago");
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + (hrs === 1 ? " hour ago" : " hours ago");
  const days = Math.round(hrs / 24);
  return days + (days === 1 ? " day ago" : " days ago");
}

/* A draft older than this is not offered for resume — coming back a week later
   to "do you want to carry on with this?" is noise, and the work has almost
   certainly been redone or abandoned. */
export const DRAFT_MAX_AGE = 3 * 24 * 60 * 60 * 1000;

export function freshDraft<T = any>(key: string): Draft<T> | null {
  const d = readDraft<T>(key);
  if (!d) return null;
  if (Date.now() - d.at > DRAFT_MAX_AGE) { clearDraft(key); return null; }
  return d;
}
